import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Suspense, lazy, useEffect, useState } from "react";

import { DefaultProviders } from "./components/providers/default.tsx";
import Navigation from "./components/navigation.tsx";
import Footer from "./components/footer.tsx";
import ScrollToTop from "./components/scroll-to-top.tsx";
import Home from "./pages/home.tsx";
import { initializePerformanceMonitoring } from "./lib/performance.ts";

const Chatbot = lazy(() => import("./components/chatbot.tsx"));
const About = lazy(() => import("./pages/about.tsx"));
const Skills = lazy(() => import("./pages/skills.tsx"));
const Projects = lazy(() => import("./pages/projects.tsx"));
const Components = lazy(() => import("./pages/components.tsx"));
const ComponentDetail = lazy(() => import("./pages/component-detail.tsx"));
const Blog = lazy(() => import("./pages/blog.tsx"));
const BlogPost = lazy(() => import("./pages/blog-post.tsx"));
const Contact = lazy(() => import("./pages/contact.tsx"));
const Services = lazy(() => import("./pages/services.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

// Fallback component for page loading
function PageLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent"></div>
        <p className="mt-4 text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

export default function App() {
  const [showChatbot, setShowChatbot] = useState(false);

  useEffect(() => {
    // Initialize performance monitoring
    initializePerformanceMonitoring();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setShowChatbot(true), 1500);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <DefaultProviders>
      <BrowserRouter>
        <ScrollToTop />
        <Navigation />
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            {/* Professional Route Sequence */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/skills" element={<Skills />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/services" element={<Services />} />
            <Route path="/components" element={<Components />} />
            <Route path="/components/:categoryId" element={<ComponentDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        {showChatbot && (
          <Suspense fallback={null}>
            <Chatbot />
          </Suspense>
        )}
        <Footer />
      </BrowserRouter>
    </DefaultProviders>
  );
}